<template>
  <div class="add-provider-page">
    <div class="header">
      <h1>Add a New Provider</h1>
      <p class="subtitle">Add a new continuing education provider to the system</p>
    </div>

    <form @submit.prevent="handleSubmit" class="provider-form">
      <ProviderForm
        ref="providerForm"
        input-id="addProviderForm"
        :require-postcode="false"
        @form-ready="handleFormReady"
        @form-data-changed="handleFormDataChanged"
      />

      <!-- Submit Button -->
      <div class="form-actions">
        <button type="submit" :disabled="isSubmitting || !isFormReady" class="submit-button">
          {{ isSubmitting ? 'Creating...' : 'Create Provider' }}
        </button>
        <button type="button" @click="handleCancel" class="cancel-button">
          Cancel
        </button>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script>
import { createProvider } from '../../services/providerService.js'
import ProviderForm from '../shared/ProviderForm.vue'

export default {
  name: 'AddProvider',
  components: {
    ProviderForm
  },
  data() {
    return {
      isSubmitting: false,
      successMessage: '',
      errorMessage: '',
      isFormReady: false,
      providerFormData: {}
    }
  },
  methods: {
    handleFormReady(isReady) {
      this.isFormReady = isReady
    },
    handleFormDataChanged(formData) {
      this.providerFormData = formData
    },
    async handleSubmit() {
      if (!this.$refs.providerForm) {
        this.errorMessage = 'Form not available.'
        return
      }

      const formData = this.$refs.providerForm.getFormData()

      // Validate required fields
      if (!formData.name || !formData.provider_type || 
          !formData.website || !formData.description) {
        this.errorMessage = 'Please fill in all required fields.'
        return
      }

      this.isSubmitting = true
      this.successMessage = ''
      this.errorMessage = ''

      try {
        await createProvider(formData)
        this.successMessage = 'Provider created successfully! Redirecting...'
        
        setTimeout(() => {
          this.$router.push({ name: 'admin' })
        }, 1500)
      } catch (error) {
        this.errorMessage = error.message || 'Failed to create provider. Please try again.'
        console.error('Error creating provider:', error)
      } finally {
        this.isSubmitting = false
      }
    },
    handleCancel() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.$router.push({ name: 'admin' })
      }
    }
  }
}
</script>

<style scoped>
.add-provider-page {
  min-height: calc(100vh - 200px);
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

h1 {
  color: var(--color-text-primary);
  font-size: 2rem;
  margin-bottom: 10px;
}

.subtitle {
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.provider-form {
  max-width: 800px;
  width: 100%;
  background-color: var(--color-surface-light);
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid var(--color-border-light);
}

.submit-button {
  padding: 12px 24px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.submit-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.submit-button:disabled {
  background-color: var(--color-border-light);
  color: var(--color-text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.cancel-button {
  padding: 12px 24px;
  background-color: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-main);
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all var(--transition-normal);
}

.cancel-button:hover {
  background-color: var(--color-surface-dark);
  border-color: var(--color-border-dark);
}

.success-message {
  margin-top: 20px;
  padding: 12px 16px;
  background-color: var(--color-success-light);
  color: var(--color-success-main);
  border-radius: 6px;
  border: 1px solid var(--color-success-main);
}

.error-message {
  margin-top: 20px;
  padding: 12px 16px;
  background-color: var(--color-error-light);
  color: var(--color-error-main);
  border-radius: 6px;
  border: 1px solid var(--color-error-main);
}


/* Responsive */
@media (max-width: 768px) {
  .add-provider-page {
    padding: 20px 10px;
  }

  .provider-form {
    padding: 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .submit-button,
  .cancel-button {
    width: 100%;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
